import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import PageHeader from "../components/PageHeader";
import DataTable from "../components/DataTable";
import LoadingState from "../components/LoadingState";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
    DialogDescription,
} from "../components/ui/dialog";
import { Building2, Plus, Loader2, Pencil, Trash2, Mail, Info, Sparkles, ShieldCheck } from "lucide-react";
import { Switch } from "../components/ui/switch";
import api from "../lib/api";
import { useAuth } from "../lib/auth";

const EMAIL_RE = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;

function emptyForm() {
    return {
        name: "",
        vat_number: "",
        address: "",
        primary_pec_address: "",
        ai_enabled: false,
        redact_sensitive_before_ai: true,
        retention_days: 365,
    };
}

export default function AdminCompaniesPage() {
    const [companies, setCompanies] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [form, setForm] = useState(emptyForm());
    const [saving, setSaving] = useState(false);
    const [mailboxSuggestions, setMailboxSuggestions] = useState([]);
    // Auth store: refresh companies list after save so the CompanyPecBanner
    // and CompanySelector immediately reflect the updated primary_pec_address.
    const refreshMe = useAuth((s) => s.refreshMe);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/companies");
            setCompanies(data || []);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Errore");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
    }, []);

    const loadMailboxSuggestions = async (companyId) => {
        if (!companyId) {
            setMailboxSuggestions([]);
            return;
        }
        try {
            const { data } = await api.get("/mailboxes", {
                headers: { "X-Company-Id": companyId },
            });
            const items = data?.items || [];
            // dedupe by pec_address, ignore empty
            const seen = new Set();
            const list = [];
            for (const m of items) {
                const a = (m.pec_address || "").toLowerCase().trim();
                if (a && !seen.has(a)) {
                    seen.add(a);
                    list.push(a);
                }
            }
            setMailboxSuggestions(list);
        } catch {
            setMailboxSuggestions([]);
        }
    };

    const openCreate = () => {
        setEditingId(null);
        setForm(emptyForm());
        setMailboxSuggestions([]);
        setDialogOpen(true);
    };

    const openEdit = (c) => {
        setEditingId(c.id);
        setForm({
            name: c.name || "",
            vat_number: c.vat_number || "",
            address: c.address || "",
            primary_pec_address: c.primary_pec_address || "",
            ai_enabled: !!c.ai_enabled,
            redact_sensitive_before_ai: c.redact_sensitive_before_ai !== false,
            retention_days: c.retention_days || 365,
        });
        loadMailboxSuggestions(c.id);
        setDialogOpen(true);
    };

    // Modal state for post-activation bulk re-analysis prompt
    const [aiActivatedInfo, setAiActivatedInfo] = useState(null);
    const [bulkPreview, setBulkPreview] = useState(null);
    const [bulkLaunching, setBulkLaunching] = useState(false);

    const save = async (ev) => {
        ev.preventDefault();
        if (!form.name.trim() || !form.vat_number.trim())
            return toast.error("Nome e P.IVA obbligatori");
        // Client-side email validation (backend re-validates)
        const pec = (form.primary_pec_address || "").trim().toLowerCase();
        if (pec !== "" && !EMAIL_RE.test(pec)) {
            return toast.error("Indirizzo PEC di riferimento non valido");
        }
        const payload = { ...form, primary_pec_address: pec === "" ? null : pec };
        // Track AI off → on transition (only for existing companies)
        const wasAiOn = editingId
            ? !!(companies.find((c) => c.id === editingId)?.ai_enabled)
            : false;
        const aiJustActivated = editingId && !wasAiOn && !!form.ai_enabled;

        setSaving(true);
        try {
            let target;
            if (editingId) {
                const { data } = await api.patch(`/admin/companies/${editingId}`, payload);
                target = data;
                toast.success("Azienda aggiornata");
            } else {
                const { data } = await api.post("/admin/companies", payload);
                target = data;
                toast.success("Azienda creata");
            }
            setDialogOpen(false);
            await load();
            // BUGFIX P1: refresh auth store so CompanyPecBanner + CompanySelector
            // pick up the freshly saved primary_pec_address. Without this, they
            // continue to show whatever was cached at login (e.g. a stale value
            // pointing to a different mailbox address).
            try { await refreshMe(); } catch (_) { /* non-blocking */ }

            if (aiJustActivated && target?.id) {
                // Fetch preview of how many PEC would be re-analyzed
                try {
                    const { data: preview } = await api.get(`/admin/companies/${target.id}/ai/reanalyze-preview`);
                    setBulkPreview(preview);
                    setAiActivatedInfo({ id: target.id, name: target.name });
                } catch {
                    // If preview endpoint fails we still notify the user AI is on.
                }
            }
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Errore");
        } finally {
            setSaving(false);
        }
    };

    const launchBulkReanalyze = async () => {
        if (!aiActivatedInfo) return;
        setBulkLaunching(true);
        try {
            await api.post(`/admin/companies/${aiActivatedInfo.id}/ai/bulk-reanalyze`);
            toast.success("Ri-analisi storiche avviata. Riceverai notifica al termine.");
            setAiActivatedInfo(null);
            setBulkPreview(null);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Errore avvio ri-analisi");
        } finally {
            setBulkLaunching(false);
        }
    };

    const remove = async (c) => {
        if (!window.confirm(`Eliminare l'azienda "${c.name}"?`)) return;
        try {
            await api.delete(`/admin/companies/${c.id}`);
            toast.success("Azienda eliminata");
            await load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Errore");
        }
    };

    const columns = [
        {
            key: "name", header: "Azienda", sortable: true,
            render: (r) => (
                <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-lg bg-[#2563EB]/10 flex items-center justify-center">
                        <Building2 className="w-4 h-4 text-[#2563EB]" />
                    </div>
                    <div>
                        <div className="text-sm font-medium text-[#172033]">{r.name}</div>
                        <div className="text-xs text-[#64748B]">{r.address || "—"}</div>
                    </div>
                </div>
            ),
        },
        { key: "vat_number", header: "P. IVA", sortable: true, render: (r) => <span className="font-mono text-xs">{r.vat_number}</span> },
        {
            key: "primary_pec_address",
            header: "PEC di riferimento",
            sortable: true,
            render: (r) => r.primary_pec_address ? (
                <span
                    className="font-mono text-xs text-[#334155]"
                    data-testid={`company-primary-pec-cell-${r.id}`}
                >
                    {r.primary_pec_address}
                </span>
            ) : <span className="text-[#94A3B8] text-xs">—</span>,
        },
        { key: "ai_enabled", header: "AI", render: (r) => r.ai_enabled ? <span className="text-[#16A34A] text-xs font-medium">Sì</span> : <span className="text-[#64748B] text-xs">No</span> },
        { key: "retention_days", header: "Ritenzione (gg)", sortable: true },
        { key: "is_active", header: "Stato", render: (r) => r.is_active ? <span className="text-[#16A34A] text-xs font-semibold">Attiva</span> : <span className="text-[#DC2626] text-xs font-semibold">Disattiva</span> },
        {
            key: "actions", header: "", render: (r) => (
                <div className="flex items-center gap-1 justify-end">
                    <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => openEdit(r)}
                        data-testid={`edit-company-${r.id}`}
                    ><Pencil className="w-4 h-4" /></Button>
                    <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => remove(r)}
                        data-testid={`delete-company-${r.id}`}
                        className="text-[#DC2626] hover:text-[#DC2626]"
                    ><Trash2 className="w-4 h-4" /></Button>
                </div>
            ),
            className: "text-right",
        },
    ];

    return (
        <div className="space-y-6">
            <PageHeader
                title="Aziende"
                subtitle="Gestisci le aziende clienti (Super Admin)."
                breadcrumbs={[{ label: "Amministrazione" }, { label: "Aziende" }]}
                actions={
                    <Button
                        onClick={openCreate}
                        data-testid="new-company-btn"
                        className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white gap-2"
                    >
                        <Plus className="w-4 h-4" />
                        Nuova azienda
                    </Button>
                }
            />

            {loading ? (
                <LoadingState />
            ) : (
                <DataTable
                    testId="companies-table"
                    columns={columns}
                    data={companies}
                    pageSize={20}
                    emptyTitle="Nessuna azienda"
                    emptyDescription="Crea la prima azienda cliente."
                    getRowKey={(r) => r.id}
                />
            )}

            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogContent className="bg-white sm:max-w-lg">
                    <DialogHeader>
                        <DialogTitle>{editingId ? "Modifica azienda" : "Nuova azienda"}</DialogTitle>
                        <DialogDescription>Compila i dati dell&apos;azienda cliente.</DialogDescription>
                    </DialogHeader>
                    <form onSubmit={save} className="space-y-4" data-testid="company-form">
                        <div>
                            <Label htmlFor="c-name">Nome</Label>
                            <Input id="c-name" data-testid="company-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1 h-10" />
                        </div>
                        <div>
                            <Label htmlFor="c-vat">Partita IVA</Label>
                            <Input id="c-vat" data-testid="company-vat-input" value={form.vat_number} onChange={(e) => setForm({ ...form, vat_number: e.target.value })} className="mt-1 h-10 font-mono" />
                        </div>
                        <div>
                            <Label htmlFor="c-addr">Indirizzo</Label>
                            <Input id="c-addr" data-testid="company-address-input" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="mt-1 h-10" />
                        </div>
                        <div>
                            <Label htmlFor="c-pec" className="flex items-center gap-1.5">
                                <Mail className="w-3.5 h-3.5 text-[#64748B]" />
                                PEC di riferimento (opzionale)
                            </Label>
                            <Input
                                id="c-pec"
                                type="text"
                                inputMode="email"
                                data-testid="company-primary-pec-input"
                                value={form.primary_pec_address}
                                onChange={(e) => setForm({ ...form, primary_pec_address: e.target.value })}
                                placeholder="admin@duca-hotel.pec.it"
                                className="mt-1 h-10 font-mono text-sm lowercase"
                            />
                            {mailboxSuggestions.length > 0 && (
                                <div
                                    className="mt-2 rounded-md border border-[#DCE3EE] bg-[#F5F7FB] p-2"
                                    data-testid="company-primary-pec-suggestions"
                                >
                                    <div className="text-[10px] uppercase tracking-wider text-[#64748B] mb-1.5">
                                        Suggerimenti dalle caselle configurate
                                    </div>
                                    <div className="flex flex-wrap gap-1.5">
                                        {mailboxSuggestions.map((addr, i) => (
                                            <button
                                                type="button"
                                                key={addr}
                                                data-testid={`company-primary-pec-suggestion-${i}`}
                                                onClick={() => setForm({ ...form, primary_pec_address: addr })}
                                                className="text-[11px] font-mono px-2 py-1 rounded-md bg-white border border-[#DCE3EE] text-[#334155] hover:border-[#2563EB] hover:text-[#2563EB] transition-colors"
                                            >
                                                {addr}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}
                            <p className="text-[11px] text-[#64748B] mt-1.5 flex items-start gap-1">
                                <Info className="w-3 h-3 mt-0.5 shrink-0" />
                                <span>
                                    Casella PEC ufficiale dell&apos;azienda. Puoi collegare più caselle per l&apos;ingestion
                                    automatica nella sezione <span className="font-medium">Integrazioni → Caselle PEC</span>.
                                </span>
                            </p>
                        </div>
                        <div>
                            <Label htmlFor="c-ret">Ritenzione (giorni)</Label>
                            <Input id="c-ret" type="number" min="30" value={form.retention_days} onChange={(e) => setForm({ ...form, retention_days: parseInt(e.target.value || "365", 10) })} className="mt-1 h-10" />
                        </div>

                        {/* AI section — explicit "0€" copy to remove the "why isn't it there?" doubt */}
                        <div className="border border-[#DCE3EE] rounded-lg p-4 bg-[#F8FAFC]" data-testid="company-ai-section">
                            <div className="flex items-center gap-2 mb-3">
                                <Sparkles className="w-4 h-4 text-[#2563EB]" />
                                <h4 className="text-sm font-semibold text-[#172033]">Analisi AI del contenuto</h4>
                            </div>

                            <div className="flex items-start justify-between gap-3">
                                <div className="flex-1 pr-3">
                                    <Label htmlFor="ai-enabled-switch" className="text-sm font-medium text-[#172033] cursor-pointer">
                                        Abilita analisi contenuto tramite AI
                                    </Label>
                                    <p className="text-[11px] text-[#64748B] mt-1 leading-relaxed">
                                        Quando attiva, ogni PEC viene analizzata da un modello linguistico
                                        (<span className="font-mono">GPT-4o</span>) per identificare:
                                    </p>
                                    <ul className="text-[11px] text-[#64748B] mt-1 ml-4 list-disc space-y-0.5">
                                        <li>Intent della comunicazione</li>
                                        <li>Richieste di pagamento / IBAN / documenti</li>
                                        <li>Segnali di frode, urgenza, impersonazione</li>
                                        <li>Deadline e importi</li>
                                    </ul>
                                    <p className="text-[11px] text-[#64748B] mt-2 leading-relaxed">
                                        Il contenuto delle PEC <span className="font-medium">non</span> viene usato per il
                                        training dei modelli. Costo per l&apos;azienda:{" "}
                                        <span className="font-semibold text-[#16A34A]">0€ (incluso)</span>.
                                    </p>
                                </div>
                                <Switch
                                    id="ai-enabled-switch"
                                    data-testid="company-ai-enabled-toggle"
                                    checked={form.ai_enabled}
                                    onCheckedChange={(v) => setForm({ ...form, ai_enabled: v })}
                                />
                            </div>

                            <div className="border-t border-[#DCE3EE] mt-3 pt-3">
                                <div className="flex items-start justify-between gap-3">
                                    <div className="flex-1 pr-3">
                                        <Label htmlFor="ai-redact-switch" className={`text-sm font-medium cursor-pointer ${form.ai_enabled ? "text-[#172033]" : "text-[#94A3B8]"}`}>
                                            <ShieldCheck className="inline w-3.5 h-3.5 mr-1 -mt-0.5" />
                                            Redigi dati sensibili prima dell&apos;invio all&apos;AI
                                        </Label>
                                        <p className={`text-[11px] mt-1 leading-relaxed ${form.ai_enabled ? "text-[#64748B]" : "text-[#94A3B8]"}`}>
                                            IBAN, P. IVA e codici fiscali vengono sostituiti con placeholder prima
                                            di inviare il testo al modello. Consigliato per contesti GDPR-sensibili.
                                        </p>
                                    </div>
                                    <Switch
                                        id="ai-redact-switch"
                                        data-testid="company-ai-redact-toggle"
                                        disabled={!form.ai_enabled}
                                        checked={form.redact_sensitive_before_ai}
                                        onCheckedChange={(v) => setForm({ ...form, redact_sensitive_before_ai: v })}
                                    />
                                </div>
                            </div>
                        </div>

                        <DialogFooter>
                            <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Annulla</Button>
                            <Button type="submit" disabled={saving} data-testid="company-save-btn" className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white">
                                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Salva"}
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            {/* Post-activation prompt: ask if user wants to re-analyze historical PEC */}
            <Dialog open={!!aiActivatedInfo} onOpenChange={(v) => { if (!v) { setAiActivatedInfo(null); setBulkPreview(null); } }}>
                <DialogContent className="bg-white sm:max-w-md" data-testid="ai-bulk-reanalyze-modal">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Sparkles className="w-4 h-4 text-[#2563EB]" />
                            AI abilitata per {aiActivatedInfo?.name}
                        </DialogTitle>
                        <DialogDescription>
                            Le nuove PEC saranno analizzate automaticamente dall&apos;AI.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="rounded-md border border-[#DCE3EE] bg-[#F5F7FB] p-3 text-sm text-[#334155]">
                        {bulkPreview ? (
                            <>
                                <div>Vuoi ri-analizzare anche le PEC storiche di questa azienda?</div>
                                <div className="mt-2 text-xs text-[#64748B]">
                                    Candidate <span className="font-semibold text-[#172033]" data-testid="bulk-reanalyze-count">{bulkPreview.candidate_count}</span> PEC su un totale di {bulkPreview.total_pec}.
                                    <br />Costo: <span className="text-[#16A34A] font-medium">0€</span>. Tempo stimato: ~<span className="font-medium">{bulkPreview.estimated_minutes}</span> minuti.
                                </div>
                            </>
                        ) : (
                            <div className="text-xs text-[#64748B]">Preparazione anteprima…</div>
                        )}
                    </div>
                    <DialogFooter>
                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => { setAiActivatedInfo(null); setBulkPreview(null); }}
                            data-testid="ai-bulk-skip-btn"
                        >
                            Solo nuove PEC
                        </Button>
                        <Button
                            type="button"
                            disabled={bulkLaunching || !bulkPreview || bulkPreview.candidate_count === 0}
                            onClick={launchBulkReanalyze}
                            data-testid="ai-bulk-confirm-btn"
                            className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white"
                        >
                            {bulkLaunching ? <Loader2 className="w-4 h-4 animate-spin" /> : "Ri-analizza anche storiche"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
